/* eslint-disable max-statements */
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { LoaderFunctionArgs, useFetcher, useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { packObjForSubmit, unpackObjForSubmit } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { doFetchPost } from "../../utils/doFetchPost";
import { ContactUs } from "./ContactUs";
import { IContactUsData } from "./ContactUsData";

export const ContactUsRouteLoader = async ({ request, params }: LoaderFunctionArgs) => {
  return params.signedIn ? doFetch(`contact-us`, { signal: request.signal }, true, true) : true
}

export const ContactUsRouteAction = async ({ request }: { request: any, params: any }) => {
  let formData = await request.formData();
  let body = unpackObjForSubmit(formData);
  return doFetchPost('contact-us', { signal: request.signal, body })
}

export const ContactUsRoute = () => {
  const data = useLoaderData() as IContactUsData;
  const auth = useAuth();
  const fetcher = useFetcher();
  const location = useLocation();
  const navigate = useNavigate();
  const userRegId = auth.isAuthenticated ? Number(auth.user?.profile.sub) : '';
  const [name, setName] = useState(data.name ?? '');
  const [email, setEmail] = useState(data.email ?? '');
  const [regId, setRegId] = useState<number | ''>(userRegId);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [captchaResponse, setCaptchaResponse] = useState('');
  const [comments, setComments] = useState('');
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated && location.pathname === '/contact-us') {
      navigate('/contact-us/signedIn');
    }
  }, [auth, location, navigate])

  useEffect(() => {
    if (showSuccessMsg) {
      setShowSuccessMsg(false);
    }
  }, [location])

  const onSetName = ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Allow letters, numbers, and spaces
    const regex = /^[A-Za-z0-9\s]*$/;
    if (regex.test(value)) {
      setName(value);
      setIsFormTouched(true);
    }
  };

  const onSetRegId = ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Regular expression to check if a string consists of only numbers
    const regex = /^\d*$/;
    const maxLength = 6;
    if (value === '' || (regex.test(value) && value.length <= maxLength)) {
      setRegId(value === '' ? '' : Number(value));
    }
  };

  const isValidEmail = (value: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(value);
  };

  const onSetEmail = ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(!!value.trim() ? value : '');
    setIsFormTouched(true);
  }

  const onSetCaptchaResponse = (captchaResponse: string | null) => {
    setCaptchaResponse(captchaResponse ?? '');
  }

  const onSetComments = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComments(e.target.value);
    setIsFormTouched(true);
  }

  const onSubmit = () => {
    const formData = packObjForSubmit({
      name: encodeURIComponent(name.trimEnd()),
      email,
      ...(regId && { regId }),
      captchaResponse,
      comments: encodeURIComponent(comments.trimEnd()),
    });

    fetcher.submit(formData, {
      method: "post"
    });

    setShowSuccessMsg(true);
  };

  const props = {
    name,
    email,
    regId,
    onSetComments,
    onSetEmail,
    onSetName,
    onSetRegId,
    onSetCaptchaResponse,
    onSubmit,
    isFormTouched,
    isValidEmail,
    captchaResponse,
    comments,
    showSuccessMsg
  }

  return <ContactUs {...props} />;
}